import { useCallback, useEffect, useRef, useState } from "react";
import { useEditor, EditorContent, useEditorState } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { AutoNbsp } from "../extensions/autoNbsp";
import { HardBreakOnEnter } from "../extensions/hardBreakOnEnter";
import { LinkExtension } from "../extensions/linkExtension";
import styled from "styled-components";
import { theme } from "../theme";

// ==================== Styled Components ====================

const EditorWrapper = styled.div`
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radius.md}px;
  overflow: hidden;

  a {
    pointer-events: none;
  }

  div.tiptap.ProseMirror {
    padding: 0 10px;
    font-size: 14px;
    font-family: ${theme.fonts.body};
    color: ${theme.colors.text};
  }

  div.tiptap.ProseMirror-focused {
    outline: none;
  }
`;

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  padding: 8px;
  background: ${theme.colors.bg};
  border-bottom: 1px solid ${theme.colors.borderLight};
`;

const ToolbarButton = styled.button`
  padding: 6px 10px;
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radius.sm}px;
  background: ${({ $active }) =>
    $active ? theme.colors.borderLight : theme.colors.surface};
  cursor: pointer;
  font-size: 13px;
  font-weight: ${({ $active }) => ($active ? "600" : "normal")};
  color: ${theme.colors.text};
  font-family: ${theme.fonts.body};
  min-width: 32px;
  transition: background 0.2s;

  &:hover(:not(:disabled)) {
    background: ${theme.colors.border};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const NbspButton = styled.button`
  padding: 6px 10px;
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radius.sm}px;
  background: ${theme.colors.surface};
  cursor: pointer;
  font-size: 13px;
  color: ${theme.colors.text};
  font-family: ${theme.fonts.body};
  min-width: 32px;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: background 0.2s;

  &:hover:not(:disabled) {
    background: ${theme.colors.borderLight};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CodePreview = styled.textarea`
  width: 100%;
  padding: 14px 10px;
  font-size: 14px;
  font-family: monospace;
  border: none;
  outline: none;
  box-sizing: border-box;
  resize: none;
  overflow: hidden;
  color: ${theme.colors.text};
  background: ${theme.colors.surface};

  &:focus {
    outline: none;
  }
`;

// ==================== Component ====================

export const RichTextEditor = ({
  options = { nbsp: true, links: true },
  blocks,
  value,
  onChange,
  showMessage,
}) => {
  const [viewMode, setViewMode] = useState("visual");
  const [codeValue, setCodeValue] = useState(value || "");
  const textareaRef = useRef(null);

  const editor = useEditor({
    extensions: [StarterKit, AutoNbsp, HardBreakOnEnter, LinkExtension],
    content: value || "",
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      onChange(html);
    },
  });

  useEffect(() => {
    if (textareaRef.current && viewMode === "code") {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height =
        textareaRef.current.scrollHeight + "px";
    }
  }, [viewMode, textareaRef]);

  // Синхронизация внешнего value с контентом редактора
  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value || "");
    }
  }, [value, editor]);

  useEffect(() => {
    if (viewMode === "code" && editor) {
      setCodeValue(editor.getHTML());
    }
  }, [editor, viewMode]);

  useEffect(() => {
    if (viewMode === "visual" && editor && codeValue !== editor.getHTML()) {
      editor.commands.setContent(codeValue);
    }
  }, [viewMode, editor, codeValue]);

  const handleApplyNbsp = useCallback(() => {
    if (viewMode === "code") return;
    editor.chain().focus().applyAutoNbsp().run();
    showMessage("Неразрывные пробелы проставлены");
  }, [editor, viewMode, showMessage]);

  const setLink = useCallback(() => {
    const previousUrl = editor.getAttributes("link").href;
    const { from, to, empty } = editor.state.selection;
    let url;

    if (empty) {
      url = window.prompt("URL", previousUrl);
    } else {
      const text = editor.state.doc.textBetween(from, to);
      const normalized = text.trim().toLowerCase().replace(/\s+/g, " ");

      const findBlock = blocks.find((block) => {
        return block.title.trim().toLowerCase() === normalized;
      });

      if (findBlock) {
        url = `/${findBlock.id}`;
      } else url = window.prompt("URL", previousUrl);
    }

    // cancelled
    if (url === null) {
      return;
    }

    // empty
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();

      return;
    }

    // update link
    try {
      editor
        .chain()
        .focus()
        .extendMarkRange("link")
        .setLink({ href: url })
        .run();
    } catch (e) {
      alert(e.message);
    }
  }, [editor, blocks]);

  const checkIsTextSelected = () => {
    const { empty } = editor.state.selection;

    return !empty;
  };

  const editorState = useEditorState({
    editor,
    selector: (ctx) => ({
      isLink: ctx.editor.isActive("link"),
      isText: checkIsTextSelected(),
    }),
  });

  if (!editor) {
    return null;
  }

  const isActiveLink = editor.isActive("link");

  return (
    <EditorWrapper>
      <Toolbar>
        {options.links && (
          <>
            <ToolbarButton
              type="button"
              $active={isActiveLink}
              disabled={
                viewMode === "code" ||
                (!editorState.isLink && !editorState.isText)
              }
              onClick={setLink}
            >
              {`${isActiveLink ? "Изменить" : "Добавить"} ссылку`}
            </ToolbarButton>
            <ToolbarButton
              type="button"
              disabled={viewMode === "code" || !editorState.isLink}
              title="Удалить ссылку"
              onClick={() => editor.chain().focus().unsetLink().run()}
            >
              Удалить ссылку
            </ToolbarButton>
          </>
        )}

        {options.nbsp && (
          <NbspButton
            type="button"
            title="Неразрывные пробелы"
            disabled={viewMode === "code"}
            onClick={handleApplyNbsp}
          >
            Неразрывные пробелы
          </NbspButton>
        )}
        <ToolbarButton
          type="button"
          onClick={() => setViewMode(viewMode === "visual" ? "code" : "visual")}
        >
          {viewMode === "visual" ? "HTML" : "ТЕКСТ"}
        </ToolbarButton>
      </Toolbar>
      {viewMode === "code" ? (
        <CodePreview
          ref={textareaRef}
          value={codeValue}
          onChange={(e) => setCodeValue(e.target.value)}
        />
      ) : (
        <EditorContent editor={editor} />
      )}
    </EditorWrapper>
  );
};
